import { useCallback, useState } from 'react';
import { NavLink, Route, Routes, Navigate } from 'react-router-dom';
import { api, formatNumber, formatRelative } from './api';
import { useAuth, usePolling } from './auth';
import { SessionReadout, Spinner } from './components/ui';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Streams from './pages/Streams';
import Trades from './pages/Trades';
import Decisions from './pages/Decisions';
import Charts from './pages/Charts';
import Backtest from './pages/Backtest';
import Options from './pages/Options';
import News from './pages/News';
import Providers from './pages/Providers';
import Settings from './pages/Settings';
import type { DashboardResponse } from './types';

export interface Toast {
  id: number;
  message: string;
  tone: 'success' | 'error';
}

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: string, tone: 'success' | 'error' = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 6000);
  }, []);
  return { toasts, push };
}

// Numbered like desk function keys; the glyph is a typographic mark, not an emoji.
const NAV = [
  { to: '/', label: 'desk', num: 'F1', end: true },
  { to: '/streams', label: 'streams', num: 'F2' },
  { to: '/trades', label: 'blotter', num: 'F3' },
  { to: '/decisions', label: 'decisions', num: 'F4' },
  { to: '/backtest', label: 'backtest', num: 'F5' },
  { to: '/options', label: 'options', num: 'F6' },
  { to: '/news', label: 'news', num: 'F7' },
  { to: '/charts', label: 'charts', num: 'F8' },
  { to: '/providers', label: 'feeds', num: 'F9' },
  { to: '/settings', label: 'config', num: 'F10' },
];

export default function App() {
  const { username, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <Spinner label="connecting to desk" />
      </div>
    );
  }
  if (!username) return <Login />;
  return <Desk username={username} onSignOut={signOut} />;
}

function Desk({ username, onSignOut }: { username: string; onSignOut: () => Promise<void> }) {
  const { toasts, push } = useToasts();
  const { data } = usePolling<DashboardResponse>(() => api.dashboard(), 30_000);
  const usd = data?.fxRates.find((rate) => rate.currency === 'USD');

  return (
    <div className="desk">
      <aside className="rail">
        <div className="rail-mark">
          <div className="wordmark">
            stocks<b>·</b>ai
          </div>
          <div className="wordmark-sub">strategy desk</div>
        </div>

        <div className="rail-section">
          <div className="rail-section-label">Market</div>
          {data ? (
            <>
              <SessionReadout label="LSE" session={data.sessions.LSE} />
              <SessionReadout label="NYSE" session={data.sessions.US} />
              <div className="readout">
                <span className="readout-key">GBP/USD</span>
                <span className="readout-val">
                  {usd ? formatNumber(1 / usd.rate, 4) : '—'}
                </span>
              </div>
              {data.regime && (
                <div className="readout">
                  <span className="readout-key">Regime</span>
                  <span
                    className={`readout-val ${data.regime.regime === 'bull' ? 'on' : data.regime.regime === 'bear' ? 'off' : ''}`}
                  >
                    {data.regime.regime.toUpperCase()}
                  </span>
                </div>
              )}
            </>
          ) : (
            <Spinner />
          )}
        </div>

        <div className="rail-section">
          <div className="rail-section-label">Engine</div>
          {data && (
            <>
              <div className="readout">
                <span className="readout-key">Scheduler</span>
                <span className={`readout-val ${data.scheduler.active ? 'on' : 'off'}`}>
                  {data.scheduler.active ? `${data.scheduler.intervalMinutes}m` : 'IDLE'}
                </span>
              </div>
              <div className="readout">
                <span className="readout-key">Last run</span>
                <span className="readout-val">{formatRelative(data.scheduler.lastRunAt)}</span>
              </div>
              <div className="readout">
                <span className="readout-key">Groq</span>
                <span className={`readout-val ${data.groqConfigured ? 'on' : 'off'}`}>
                  {data.groqConfigured ? 'LIVE' : 'NO KEY'}
                </span>
              </div>
            </>
          )}
        </div>

        <nav className="nav" aria-label="Sections">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              <span className="nav-num">{item.num}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="rail-foot">
          <div>
            operator <span style={{ color: 'var(--ink-dim)' }}>{username}</span>
          </div>
          <button className="key key-sm" type="button" onClick={() => void onSignOut()}>
            sign out
          </button>
          <div style={{ color: 'var(--ink-faint)' }}>paper only · no live orders</div>
        </div>
      </aside>

      <main className="work">
        <Routes>
          <Route path="/" element={<Dashboard pushToast={push} />} />
          <Route path="/streams" element={<Streams pushToast={push} />} />
          <Route path="/trades" element={<Trades />} />
          <Route path="/decisions" element={<Decisions />} />
          <Route path="/backtest" element={<Backtest pushToast={push} />} />
          <Route path="/options" element={<Options pushToast={push} />} />
          <Route path="/news" element={<News pushToast={push} />} />
          <Route path="/charts" element={<Charts />} />
          <Route path="/charts/:symbol" element={<Charts />} />
          <Route path="/analysis" element={<Navigate to="/charts" replace />} />
          <Route path="/providers" element={<Providers pushToast={push} />} />
          <Route path="/settings" element={<Settings pushToast={push} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast${toast.tone === 'error' ? ' toast-error' : ''}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}
